/** Month calendar of one deputy's schedule, with suggested hours and a day editor. */
import { useState } from 'react';
import { api } from './api.ts';
import { ErrorBox, useAction } from './ui.tsx';

export interface Entry {
  user_id: string;
  day: string;
  kind: 'working' | 'flexible' | 'off';
  start_time: string | null;
  end_time: string | null;
  source: 'deputy' | 'supervisor';
  note: string | null;
  set_by_name: string;
}
export interface Suggestion {
  start: string;
  end: string;
  label: string;
  rate: number | null;
  n: number;
  blocked?: string;
}

/** A court date someone is scheduled for (security, testifying…). */
export interface CourtItem {
  id: string;
  kind: string;
  court_name: string;
  courtroom: string | null;
  starts_at: string;
  ends_at: string;
  role: string;
  defendant: string | null;
}
const ROLE: Record<string, string> = { security: 'Court security', testifying: 'Testify', transport: 'Transport', other: 'Court' };
const localDay = (iso: string) => new Date(iso).toLocaleDateString('en-CA');
const clock = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(':00', '');

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const t12 = (hhmm: string | null) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
};
export const hours = (e: { start_time: string | null; end_time: string | null }) => (e.start_time && e.end_time ? `${t12(e.start_time)}–${t12(e.end_time)}` : '');
const addMonth = (m: string, d: number) => {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  const t = new Date(Date.UTC(y, mm - 1 + d, 1));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}`;
};
const monthName = (m: string) => new Date(`${m}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

export function ScheduleCalendar({
  userId,
  month,
  today,
  entries,
  suggestions,
  supervisor,
  readOnly,
  onMonth,
  onSaved,
  court = [],
  onCourt,
}: {
  userId: string;
  month: string;
  today: string;
  entries: Entry[];
  suggestions: Record<string, Suggestion>;
  /** Viewer is a supervisor (can set and lock any day). */
  supervisor: boolean;
  /** View only: days can't be opened for editing. */
  readOnly?: boolean;
  onMonth: (m: string) => void;
  onSaved: () => void;
  court?: CourtItem[];
  onCourt?: (id: string) => void;
}) {
  const courtByDay = new Map<string, CourtItem[]>();
  for (const c of court) courtByDay.set(localDay(c.starts_at), [...(courtByDay.get(localDay(c.starts_at)) ?? []), c]);
  const [sel, setSel] = useState<string | null>(null);
  const byDay = new Map(entries.map((e) => [e.day, e]));
  const first = new Date(`${month}-01T12:00:00Z`);
  const lead = first.getUTCDay();
  const days: (string | null)[] = Array(lead).fill(null);
  for (let d = new Date(first); d.getUTCMonth() === first.getUTCMonth(); d.setUTCDate(d.getUTCDate() + 1)) days.push(d.toISOString().slice(0, 10));
  while (days.length % 7) days.push(null);

  return (
    <div className="cal">
      <div className="cal-head">
        <button className="btn sm" onClick={() => onMonth(addMonth(month, -1))} aria-label="Previous month">
          ‹
        </button>
        <h2>{monthName(month)}</h2>
        <button className="btn sm" onClick={() => onMonth(addMonth(month, 1))} aria-label="Next month">
          ›
        </button>
        <span className="cal-legend">
          <i className="k-working" /> Working <i className="k-flexible" /> Flexible <i className="k-off" /> Off <i className="k-sup" /> Set by supervisor <i className="k-court" /> Court
        </span>
      </div>
      <div className="cal-grid" role="grid" aria-label={`Schedule for ${monthName(month)}`}>
        {WD.map((w) => (
          <div key={w} className="cal-wd">
            {w}
          </div>
        ))}
        {days.map((d, i) => {
          if (!d) return <div key={`x${i}`} className="cal-day empty" />;
          const e = byDay.get(d);
          const s = suggestions[d];
          const past = d < today;
          return (
            <button
              key={d}
              type="button"
              className={`cal-day ${e ? `k-${e.kind}` : ''} ${e?.source === 'supervisor' ? 'sup' : ''} ${d === today ? 'today' : ''} ${d === sel ? 'sel' : ''} ${past ? 'past' : ''}`}
              onClick={() => !readOnly && setSel(d)}
              aria-label={`${d}${e ? `: ${e.kind}` : ''}`}
            >
              <span className="cal-num">{Number(d.slice(8))}</span>
              {e ? (
                <span className="cal-what">
                  {e.kind === 'working' ? hours(e) || 'Working' : e.kind === 'flexible' ? 'Flexible' : 'Off'}
                  {e.source === 'supervisor' && <span className="cal-lock" title={`Set by ${e.set_by_name}`}>●</span>}
                </span>
              ) : s?.blocked ? (
                <span className="cal-sug">{s.blocked.replace(' is a blocked holiday', '').replace(' service is blocked', ' — no service')}</span>
              ) : (
                !past && s && !courtByDay.get(d) && <span className="cal-sug">Try {t12(s.start)}–{t12(s.end)}</span>
              )}
              {(courtByDay.get(d) ?? []).map((c) => (
                <span
                  key={c.id + c.role}
                  className="cal-court"
                  title={`${ROLE[c.role] ?? 'Court'} — ${c.court_name}${c.courtroom ? `, ${c.courtroom}` : ''}${c.defendant ? ` (${c.defendant})` : ''}`}
                  onClick={onCourt ? (ev) => (ev.stopPropagation(), onCourt(c.id)) : undefined}
                >
                  <b>{clock(c.starts_at)}</b>
                  <span className="cc-role"> {ROLE[c.role] ?? 'Court'}</span>
                </span>
              ))}
            </button>
          );
        })}
      </div>
      {sel && <DayEditor key={sel} userId={userId} day={sel} entry={byDay.get(sel) ?? null} suggestion={suggestions[sel]} supervisor={supervisor} onClose={() => setSel(null)} onSaved={onSaved} />}
    </div>
  );
}

function DayEditor({ userId, day, entry, suggestion, supervisor, onClose, onSaved }: { userId: string; day: string; entry: Entry | null; suggestion?: Suggestion; supervisor: boolean; onClose: () => void; onSaved: () => void }) {
  const [start, setStart] = useState(entry?.start_time ?? suggestion?.start ?? '17:00');
  const [end, setEnd] = useState(entry?.end_time ?? suggestion?.end ?? '21:00');
  const [note, setNote] = useState(entry?.note ?? '');
  const a = useAction();
  const locked = !supervisor && entry?.source === 'supervisor';
  const label = new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
  const save = (kind: 'working' | 'flexible' | 'off' | 'cleared') =>
    a.run(async () => {
      await api.post(`/api/schedule/${userId}/${day}`, { kind, start: kind === 'working' ? start : undefined, end: kind === 'working' ? end : undefined, note: note.trim() || undefined });
      onSaved();
      onClose();
    });
  return (
    <div className="day-ed" data-unsaved="true" role="dialog" aria-label={`Schedule for ${label}`}>
      <div className="day-ed-h">
        <h3>{label}</h3>
        <button className="btn sm ghost" onClick={onClose}>
          Close
        </button>
      </div>
      {entry && (
        <p className="small">
          Now: <strong>{entry.kind === 'working' ? `Working ${hours(entry)}` : entry.kind === 'flexible' ? 'Flexible' : 'Off'}</strong>
          {entry.note ? ` — ${entry.note}` : ''} · set by {entry.set_by_name}
          {entry.source === 'supervisor' ? ' (supervisor)' : ''}
        </p>
      )}
      {suggestion &&
        (suggestion.blocked ? (
          <p className="small muted">{suggestion.blocked}.</p>
        ) : (
          <p className="small muted">
            Suggested: <strong>{suggestion.label}</strong>
            {suggestion.rate != null ? ` — ${suggestion.rate}% of attempts served at that time on this weekday (${suggestion.n} attempts).` : ' — not enough history yet; evenings usually catch people home.'}
          </p>
        ))}
      {locked ? (
        <p className="alert warn">Your supervisor set this day. Ask them to change it.</p>
      ) : (
        <>
          <div className="day-ed-row">
            <label>
              From
              <input type="time" value={start} onChange={(e) => setStart(e.target.value)} aria-label="Working from" />
            </label>
            <label>
              To
              <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} aria-label="Working until" />
            </label>
            {suggestion && !suggestion.blocked && (
              <button type="button" className="btn sm ghost" onClick={() => (setStart(suggestion.start), setEnd(suggestion.end))}>
                Use suggested
              </button>
            )}
          </div>
          <label className="day-ed-note">
            Note (optional)
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. court in the morning" maxLength={300} />
          </label>
          <div className="day-ed-btns">
            <button className="btn primary" disabled={a.busy} onClick={() => save('working')}>
              {supervisor ? 'Schedule working' : 'I’m working'} {start && end ? `${t12(start)}–${t12(end)}` : ''}
            </button>
            <button className="btn" disabled={a.busy} onClick={() => save('flexible')}>
              {supervisor ? 'Flexible' : 'I’m flexible'}
            </button>
            <button className="btn" disabled={a.busy} onClick={() => save('off')}>
              {supervisor ? 'Off' : 'I’m off'}
            </button>
            {entry && (
              <button className="btn ghost" disabled={a.busy} onClick={() => save('cleared')}>
                Clear
              </button>
            )}
          </div>
        </>
      )}
      <ErrorBox error={a.error} />
    </div>
  );
}
